/**
 * Design tokens for tool components
 * Color direction (obsidian/design-system/README.md): green/emerald anchors the
 * brand, gray carries neutrals, amber/red carry warnings. Other families are
 * allowed in supporting roles; keep green dominant on any given screen.
 */

import { StatusLevel, RecommendationVariant } from '@/components/tools/shared/types'

/**
 * Status level to gradient mapping
 * Families are fixed by status semantics (green good, amber caution, red critical)
 */
export const statusGradients: Record<StatusLevel, string> = {
  optimal: 'from-green-600 via-green-500 to-emerald-600',
  acceptable: 'from-yellow-500 via-amber-500 to-yellow-600',
  warning: 'from-amber-600 via-orange-500 to-amber-700',
  critical: 'from-red-600 via-rose-500 to-red-700',
  neutral: 'from-gray-600 via-gray-500 to-slate-600',
}

/**
 * Status level to background color (for cards)
 */
export const statusBackgrounds: Record<StatusLevel, string> = {
  optimal: 'bg-green-50',
  acceptable: 'bg-yellow-50',
  warning: 'bg-amber-50',
  critical: 'bg-red-50',
  neutral: 'bg-gray-50',
}

/**
 * Status level to border color
 */
export const statusBorders: Record<StatusLevel, string> = {
  optimal: 'border-green-200',
  acceptable: 'border-yellow-200',
  warning: 'border-amber-200',
  critical: 'border-red-200',
  neutral: 'border-gray-200',
}

/**
 * Status level to text color
 */
export const statusTextColors: Record<StatusLevel, string> = {
  optimal: 'text-green-700',
  acceptable: 'text-yellow-700',
  warning: 'text-amber-700',
  critical: 'text-red-700',
  neutral: 'text-gray-700',
}

/**
 * Recommendation variant to color mapping
 * Uses green-based palette with amber/red for warnings
 */
export const recommendationColors: Record<RecommendationVariant, {
  background: string
  border: string
  text: string
  icon: string
}> = {
  success: {
    background: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-900',
    icon: 'text-green-600',
  },
  info: {
    background: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-900',
    icon: 'text-gray-600',
  },
  warning: {
    background: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-900',
    icon: 'text-amber-600',
  },
  danger: {
    background: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-900',
    icon: 'text-red-600',
  },
}

/**
 * Tool section colors (replacing blue/purple/cyan violations)
 * All use green/emerald/teal/gray spectrum
 */
export const sectionColors = {
  primary: {
    background: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-900',
    icon: 'text-green-600',
  },
  secondary: {
    background: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-900',
    icon: 'text-emerald-600',
  },
  tertiary: {
    background: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-900',
    icon: 'text-teal-600',
  },
  neutral: {
    background: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-900',
    icon: 'text-gray-600',
  },
} as const

/**
 * Supporting-role-only color families (see design-system README).
 * Fine for data viz, illustrations, or an earned accent; they should not
 * become the dominant color of a surface. Suggested brand-adjacent swaps:
 */
export const DEPRECATED_COLORS = {
  blue: 'Supporting roles only - emerald or teal for brand surfaces',
  purple: 'Supporting roles only - gray or emerald for brand surfaces',
  pink: 'Supporting roles only - green or emerald for brand surfaces',
  cyan: 'Supporting roles only - teal for brand surfaces',
  indigo: 'Supporting roles only - gray for brand surfaces',
} as const
